import { useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { PageHeader, Loading } from "../components/common";
import { HistoryList } from "../components/ProjectBits";
import { UsersTab } from "../components/admin/UsersTab";
import { SettingsTab } from "../components/admin/SettingsTab";
import { IntegrationsTab } from "../components/admin/IntegrationsTab";
import { ImportTab } from "../components/admin/ImportTab";
import { EmailTab } from "../components/admin/EmailTab";
import { BaselinesTab, RecordsTab } from "../components/admin/RecordsTabs";
import { TemplateAdoption } from "../components/TemplateAdoption";
import { GmCover } from "../components/admin/GmCover";
import { BankAdmins, SetupCompleteness } from "../components/Structure";
import { Button } from "../components/ui/button";
import { downloadFile } from "../components/PaymentLog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function AuditTab() {
  const { data, loading } = useApi("/admin/audit");
  if (loading && !data) return <Loading />;
  return (
    <div className="rounded-lg border bg-card p-4" data-testid="audit-tab">
      <HistoryList items={(data || []).map((d) => ({ ...d, field: `${d.project_name ? `${d.project_name} · ` : d.entity !== "project" ? `${d.entity} · ` : ""}${d.field}` }))} />
    </div>
  );
}

export function AdminsOnly() {
  return (
    <div className="mx-auto max-w-md py-24 text-center" data-testid="admins-only">
      <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-foreground"><Lock className="h-6 w-6" /></div>
      <h1 className="font-heading text-2xl font-bold" data-testid="admins-only-message">Admins only</h1>
      <p className="mt-2 text-sm text-muted-foreground">This area is for EPIL administrators.</p>
    </div>
  );
}

export default function Admin() {
  const { isAdmin } = useAuth();
  const [sp, setSp] = useSearchParams();
  if (!isAdmin) return <AdminsOnly />;
  const tab = sp.get("tab") || "users";
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Admin" title="Administration" subtitle="Users and roles, settings, integration keys, workbook import and the full audit trail." />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import">Import</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email">Email</TabsTrigger>
          <TabsTrigger value="baselines" data-testid="admin-tab-baselines">Baselines</TabsTrigger>
          <TabsTrigger value="records" data-testid="admin-tab-records">Records</TabsTrigger>
          <TabsTrigger value="payment-log" data-testid="admin-tab-payment-log">Payment log</TabsTrigger>
          <TabsTrigger value="downloads" data-testid="admin-tab-downloads">Downloads</TabsTrigger>
          <TabsTrigger value="setup" data-testid="admin-tab-setup">Set-up completeness</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /><GmCover /><BankAdmins /></TabsContent>
        <TabsContent value="setup"><SetupCompleteness /></TabsContent>
        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
        <TabsContent value="import"><ImportTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="baselines"><BaselinesTab /></TabsContent>
        <TabsContent value="records"><RecordsTab /></TabsContent>
        <TabsContent value="payment-log"><TemplateAdoption /></TabsContent>
        <TabsContent value="downloads">
          <div className="rounded-xl border bg-card p-6" data-testid="admin-downloads">
            <div className="eyebrow">Feedback tracking</div>
            <h2 className="mt-1 font-heading text-lg font-bold">JV_Hub_Feedback – dev response.xlsx</h2>
            <p className="mt-1 text-sm text-muted-foreground">Your feedback file with Status, Owner, Dev notes and Planned change for every item, and answers or recommendations for the open questions.</p>
            <Button className="mt-4" onClick={() => downloadFile("/admin/downloads/feedback-response.xlsx", "JV_Hub_Feedback – dev response.xlsx")} data-testid="download-feedback-response">Download dev response</Button>
          </div>
        </TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
